import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AnyoneLinkStateDto,
  SetAnyoneLinkDto,
} from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../../locales/messages";
import { reviewTokens } from "../../theme";
import { copyToClipboard } from "../copyToClipboard";
import { ExpiryChip } from "./ExpiryChip";
import { isoToLocalInput, localInputToIso } from "./dateInput";

interface Props {
  anyoneLink: AnyoneLinkStateDto | undefined;
  onSet: (body: SetAnyoneLinkDto) => Promise<AnyoneLinkStateDto | undefined>;
}

export function AnyoneLinkSection({ anyoneLink, onSet }: Props) {
  const { t } = useTranslation();
  const [access, setAccess] = useState<AnyoneLinkStateDto.AccessEnum>(
    anyoneLink?.access ??
      (AnyoneLinkStateDto.AccessEnum.DISABLED as AnyoneLinkStateDto.AccessEnum),
  );
  const [expiresAtInput, setExpiresAtInput] = useState(
    isoToLocalInput(anyoneLink?.expiresAt),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAccess(
      anyoneLink?.access ??
        (AnyoneLinkStateDto.AccessEnum
          .DISABLED as AnyoneLinkStateDto.AccessEnum),
    );
    setExpiresAtInput(isoToLocalInput(anyoneLink?.expiresAt));
  }, [anyoneLink?.access, anyoneLink?.expiresAt]);

  const handleSave = async () => {
    setSaving(true);
    const iso = localInputToIso(expiresAtInput);
    await onSet({
      access: access as unknown as SetAnyoneLinkDto.AccessEnum,
      expiresAt: (iso ?? null) as unknown as string,
    });
    setSaving(false);
  };

  const isEnabled =
    (access as unknown as string) !==
    (AnyoneLinkStateDto.AccessEnum.DISABLED as unknown as string);

  return (
    <Box>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: `${reviewTokens.radiusSm}px`,
            bgcolor: isEnabled ? reviewTokens.brandSoft : reviewTokens.slate100,
            color: isEnabled ? reviewTokens.brand : reviewTokens.slate500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color 0.15s ease",
          }}
        >
          <PublicOutlinedIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {t(messages.Anyone_With_Link())}
            </Typography>
            {isEnabled ? (
              <ExpiryChip
                expiresAt={anyoneLink?.expiresAt}
                testId="anyone-link-expiry"
              />
            ) : null}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {isEnabled
              ? "Anyone with the link can access this flyer review."
              : "Only invited people can access this flyer review."}
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ sm: "center" }}
      >
        <TextField
          size="small"
          select
          label={t(messages.Permission())}
          value={access}
          onChange={(e) =>
            setAccess(
              e.target.value as unknown as AnyoneLinkStateDto.AccessEnum,
            )
          }
          sx={{ width: { xs: "100%", sm: 160 } }}
          data-testid="anyone-link-access"
        >
          <MenuItem
            value={
              AnyoneLinkStateDto.AccessEnum.DISABLED as unknown as string
            }
          >
            DISABLED
          </MenuItem>
          <MenuItem
            value={AnyoneLinkStateDto.AccessEnum.VIEW as unknown as string}
          >
            VIEW
          </MenuItem>
          <MenuItem
            value={AnyoneLinkStateDto.AccessEnum.EDIT as unknown as string}
          >
            EDIT
          </MenuItem>
        </TextField>
        <TextField
          size="small"
          type="datetime-local"
          label={t(messages.Expires_At())}
          InputLabelProps={{ shrink: true }}
          value={expiresAtInput}
          onChange={(e) => setExpiresAtInput(e.target.value)}
          sx={{ flex: 1 }}
          data-testid="anyone-link-expires"
        />
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleSave}
          disabled={saving}
          data-testid="anyone-link-save"
          sx={{ minWidth: 80 }}
        >
          {t(messages.Save())}
        </Button>
      </Stack>

      {anyoneLink?.url ? (
        <Box
          data-testid="anyone-link-url-row"
          sx={{
            mt: 1.5,
            p: 1.25,
            borderRadius: `${reviewTokens.radiusMd}px`,
            bgcolor: reviewTokens.bgMuted,
            border: `1px solid ${reviewTokens.border}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              fontFamily: reviewTokens.fontMono,
              fontSize: 12,
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={anyoneLink.url}
          >
            {anyoneLink.url}
          </Typography>
          <Tooltip title={t(messages.Copy_Link())}>
            <IconButton
              size="small"
              onClick={() =>
                copyToClipboard(anyoneLink.url || "", t(messages.Copied()))
              }
              data-testid="anyone-link-copy"
              sx={{ color: "text.secondary" }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
    </Box>
  );
}
